/**
 * Tiny JWT decoder — no signature verification, just claim extraction.
 * Server-side validation is the source of truth; the client only reads `exp`
 * to decide whether to refresh proactively.
 */

import { normalizeIana } from './timezone';

export interface JwtClaims {
  exp?: number;
  sub?: string;
  tenant?: string;
  email?: string;
  zoneinfo?: string;
  given_name?: string;
  family_name?: string;
  /**
   * Identity server emits this as `orgRoles` (camelCase) — single role string,
   * an English label ("Practitioner" / "OrgAdmin").
   *
   * Do NOT map this onto `UserData.role`. That field is the numeric backend
   * role ID ("0".."3") every gate compares against — see roles.ts. PROD-1767
   * removed exactly that mapping.
   */
  orgRoles?: string;
  /** Identity server emits this as `orgId` (camelCase). */
  orgId?: string;
  [k: string]: unknown;
}

export function decodeJwt(token: string): JwtClaims | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    return JSON.parse(atob(padded)) as JwtClaims;
  } catch {
    return null;
  }
}

/**
 * True if the token is missing `exp`, already expired, or expires within
 * `marginSec`. The margin lets us refresh proactively before a request fires
 * with a token that would 401.
 */
export function isExpiringSoon(token: string, marginSec = 30): boolean {
  const claims = decodeJwt(token);
  if (!claims?.exp) return true;
  const nowSec = Math.floor(Date.now() / 1000);
  return claims.exp - nowSec < marginSec;
}

/**
 * Build a UserData record from JWT claims. Used as a floor in login() so we
 * always have userId / orgId / email / name even if the GetUserDataToken API
 * call fails. The API response (richer fields like practitionerId,
 * organizationLocations, systemPermissions) is merged on top.
 *
 * Note: `role` is deliberately NOT populated here. The JWT's `orgRoles` claim
 * is an English label space ("Practitioner" / "OrgAdmin"); `user.role` is the
 * numeric backend role ID ("0".."3") that every role gate compares against —
 * see roles.ts, which documents the incident where mixing the two silently
 * hid the Logs tab from every real OrgAdmin. Writing `orgRoles` into `role`
 * put a value there that no gate can match, which reads as "not a doctor"
 * rather than "unknown" and so *widens* the provider-scoped scheduling
 * queries. Leaving it undefined is honest: the api-client omits the `RoleId`
 * header (it is already guarded), and the real numeric role arrives from
 * GetUserDataToken on the success path, which merges over this floor.
 *
 * Note: the JWT carries NO practiceId claim — that comes from the
 * GetUserDataToken response. If profile fetch fails, practiceId remains
 * undefined and the api-client omits the `PracticeId` header. This is
 * intentional (PROD-773): the previous DEV_CONFIG fallback would silently
 * cross tenant boundaries for any real user whose profile fetch failed.
 */
export function userDataFromToken(
  token: string,
): import('./auth-api').UserData | null {
  const c = decodeJwt(token);
  if (!c?.sub || !c.orgId) return null;
  return {
    userId: c.sub,
    firstName: c.given_name,
    lastName: c.family_name,
    organizationId: c.orgId,
    email: c.email,
    // JWT `zoneinfo` claim — Flutter login_data_source_impl.dart:193 / 218
    // pulls this for the timezone-mismatch check post-login. Normalize it the
    // SAME way the device tz is normalized (`Asia/Calcutta` → `Asia/Kolkata`),
    // or the mismatch check compares a normalized device tz against a raw JWT
    // claim and fires a spurious popup for India users.
    timeZone: normalizeIana(c.zoneinfo),
  };
}
